// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { useCallback, useEffect, useState } from 'react'
import * as api from '../../api/client'
import ManufacturerDrugPicker, { type DrugGrant } from './ManufacturerDrugPicker'

/**
 * D-PROV-1 WS2 — optional drug filter for a PROVIDER account.
 *
 * Mirrors the manufacturer drug panel but slimmer: a provider already sees full patient
 * information, so there is no PHI toggle and no agreement reference — just the drug
 * selection. Leaving it empty means no drug filter (the provider sees every prescription
 * their prescriber scope already allows); adding drugs narrows every provider-portal
 * surface to those drugs, on top of the prescriber scope.
 *
 * Reuses the same picker and the same GET/PUT /users/{id}/drug-access endpoint as the
 * manufacturer, writing the same account_drug_access rows — the only difference is the UI
 * omits the PHI controls and sends phi_access = 'masked' (inert for a provider, whose
 * reads are not masked).
 */
export default function ProviderDrugFilterSection({ userId, roles, canWrite, onChanged }: {
  userId: string
  roles: { id: string; name: string }[]
  canWrite: boolean
  onChanged?: () => void
}) {
  // Match on role NAME (not the permission-derived legacy role, which lags a role assigned
  // seconds ago). Show for Provider and Provider Nurse.
  const show = roles.some(r => r.name === 'Provider' || r.name === 'Provider Nurse')

  const [grants, setGrants] = useState<DrugGrant[]>([])
  const [dirty, setDirty] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const d = await api.getUserDrugAccess(userId)
      setGrants(d.grants as DrugGrant[]); setDirty(false)
    } catch {
      // No grant record is normal — every provider starts with none. Not an error.
      setGrants([]); setDirty(false)
    }
  }, [userId])

  useEffect(() => { if (show) load() }, [show, load])
  if (!show) return null

  async function save() {
    setBusy(true); setErr(null); setNote(null)
    try {
      const res = await api.setUserDrugAccess(userId, { grants, phi_access: 'masked' })
      setNote(res.count > 0 ? `Filtering to ${res.count} drug${res.count === 1 ? '' : 's'}.` : 'Drug filter cleared — this provider sees all their prescriptions.')
      setDirty(false); onChanged?.()
    } catch (e: any) {
      setErr(e?.message || 'Could not save the drug filter.')
    } finally { setBusy(false) }
  }

  return (
    <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid #e2e8f0' }}>
      <h4 style={{ fontSize: 13, fontWeight: 600, margin: '0 0 4px' }}>Drug filter (optional)</h4>
      <p style={{ fontSize: 12.5, color: '#64748b', margin: '0 0 8px', lineHeight: 1.5 }}>
        Restrict this provider to specific medications. Search by NDC, drug name, brand, or
        manufacturer. Leave empty to show <strong>all</strong> of this provider's prescriptions;
        any drugs selected here narrow every provider-portal screen to those drugs, on top of the
        prescriber access above.
      </p>

      {err && <div role="alert" style={{ fontSize: 12.5, color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '7px 10px', marginBottom: 8 }}>{err}</div>}
      {note && <div style={{ fontSize: 12.5, color: '#166534', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '7px 10px', marginBottom: 8 }}>{note}</div>}

      <ManufacturerDrugPicker
        value={grants}
        onChange={g => { setGrants(g); setDirty(true) }}
        disabled={!canWrite || busy}
      />

      {canWrite && (
        <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={save} disabled={!dirty || busy}
            style={{ padding: '7px 14px', borderRadius: 7, border: 'none', background: dirty && !busy ? '#134e4a' : '#cbd5e1', color: '#fff', fontSize: 13, fontWeight: 600, cursor: dirty && !busy ? 'pointer' : 'default' }}>
            {busy ? 'Saving…' : 'Save drug filter'}
          </button>
          {grants.length > 0 && (
            <button onClick={() => { setGrants([]); setDirty(true) }} disabled={busy}
              style={{ padding: '7px 12px', borderRadius: 7, border: '1px solid #cbd5e1', background: '#fff', color: '#475569', fontSize: 13, cursor: 'pointer' }}>
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  )
}
